import React, { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSupabase } from '../contexts/SupabaseContext';
import UserNavBar from '../components/UserNavBar';
import { Submission } from '../lib/supabaseClient';
import withIdleTimer from '../hoc/withIdleTimer';

// Unique ID to track component instances
const COMPONENT_ID = `UserDashboard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const UserDashboard: React.FC = () => {
  console.debug(`[${COMPONENT_ID}] Component rendering at ${new Date().toISOString()}`);
  
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const [submissions, setSubmissions] = React.useState<Submission[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  
  // Add a ref to track if a fetch is in progress
  const isFetchingRef = useRef(false);
  
  // Log auth state on every render
  console.debug(`[${COMPONENT_ID}] Auth State: { authLoading: ${authLoading}, userId: ${user?.id || 'null'} } at ${new Date().toISOString()}`);
  // Log submissions loading state on every render
  console.debug(`[${COMPONENT_ID}] Submissions Loading State: { isLoading: ${isLoading}, submissionsCount: ${submissions.length} } at ${new Date().toISOString()}`);
  
  // Memoize the Supabase client
  const supabase = useSupabase();

  // Memoize the fetch function to avoid recreating it on each render
  const fetchSubmissions = useCallback(async () => {
    // Skip if already fetching
    if (isFetchingRef.current) {
      console.debug(`[${COMPONENT_ID}] fetchSubmissions already in progress, skipping duplicate call at ${new Date().toISOString()}`);
      return;
    }
    
    // Skip if user is not available
    if (!user) {
      console.debug(`[${COMPONENT_ID}] fetchSubmissions called with no user, skipping`);
      return;
    }

    const userId = user.id;
    const fetchStartTime = new Date().toISOString();
    console.debug(`[${COMPONENT_ID}] fetchSubmissions started at ${fetchStartTime} for userId: ${userId}`);
    
    isFetchingRef.current = true;
    setIsLoading(true);
    console.debug(`[${COMPONENT_ID}] fetchSubmissions set isLoading=true at ${new Date().toISOString()}`);
    
    console.debug(`[${COMPONENT_ID}] fetchSubmissions executing supabase query for userId: ${userId}`);
    try {
      const queryStartTime = performance.now();
      const { data, error } = await supabase
        .from('submissions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      const queryEndTime = performance.now();
      
      console.debug(`[${COMPONENT_ID}] fetchSubmissions supabase query completed in ${(queryEndTime - queryStartTime).toFixed(2)}ms`);
      
      if (error) {
        console.error(`[${COMPONENT_ID}] Error fetching submissions: [${error.code}] ${error.message}`, error);
        return;
      }

      console.debug(`[${COMPONENT_ID}] fetchSubmissions successful, got ${data?.length || 0} submissions`);
      setSubmissions((data || []) as Submission[]);
      console.debug(`[${COMPONENT_ID}] fetchSubmissions set submissions state at ${new Date().toISOString()}`);
    } catch (err) {
      console.error(`[${COMPONENT_ID}] Unexpected error in fetchSubmissions:`, err);
    } finally {
      setIsLoading(false);
      console.debug(`[${COMPONENT_ID}] fetchSubmissions set isLoading=false at ${new Date().toISOString()}`);
      
      isFetchingRef.current = false;
      const fetchEndTime = new Date().toISOString();
      console.debug(`[${COMPONENT_ID}] fetchSubmissions completed at ${fetchEndTime} (${Date.parse(fetchEndTime) - Date.parse(fetchStartTime)}ms total)`);
    }
  }, [user, supabase]);

  // Update the dependency array to only include user?.id instead of user and remove authLoading
  React.useEffect(() => {
    console.debug(`[${COMPONENT_ID}] useEffect triggered with userId: ${user?.id || 'null'} at ${new Date().toISOString()}`);
    
    // Only fetch if we have a user ID and auth loading is done
    if (!authLoading && user?.id) {
      console.debug(`[${COMPONENT_ID}] Calling fetchSubmissions for userId: ${user.id}`);
      fetchSubmissions();
    } else {
      console.debug(`[${COMPONENT_ID}] Skipping fetchSubmissions because ${authLoading ? 'authLoading=true' : 'user=null'} at ${new Date().toISOString()}`);
    }
  }, [user?.id, fetchSubmissions, authLoading]);

  const getStatusColor = (status: string) => {
    const colors = {
      DRAFT: 'bg-gray-200',
      SUBMISSION: 'bg-blue-200',
      ADMINISTRATIVE_REVIEW: 'bg-yellow-200',
      IRB_REVIEW: 'bg-purple-200',
      DECISION_ISSUED: 'bg-green-200'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-200';
  };

  const handleLogout = async () => {
    console.debug(`[${COMPONENT_ID}] handleLogout called at ${new Date().toISOString()}`);
    try {
      await signOut();
      console.debug(`[${COMPONENT_ID}] signOut successful, navigating to /auth`);
      navigate('/signin');
    } catch (error) {
      console.error(`[${COMPONENT_ID}] Error signing out:`, error);
    }
  };

  // Show loading state while auth is initializing or submissions are loading
  if (authLoading || isLoading) {
    console.debug(`[${COMPONENT_ID}] Rendering loading state: { authLoading: ${authLoading}, isLoading: ${isLoading} } at ${new Date().toISOString()}`);
    return (
      <div className="flex flex-col h-screen">
        <UserNavBar user={user} onSignOut={handleLogout} hideDashboardLink={true} />
        <div className="flex-1 bg-gray-50 mt-16">
          <div className="p-8 text-center">
            <p className="text-gray-500">Loading your submissions...refresh your browser tab if this takes too long.</p>
          </div>
        </div>
      </div>
    );
  }

  console.debug(`[${COMPONENT_ID}] Rendering dashboard with ${submissions.length} submissions at ${new Date().toISOString()}`);
  return (
    <div className="flex flex-col h-screen">
      <UserNavBar user={user} onSignOut={handleLogout} hideDashboardLink={true} />

      {/* Main Content - Adjusted for top navbar */}
      <div className="flex-1 bg-gray-50 mt-16">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold">My Submissions</h1>
            <button
              onClick={() => navigate('/new-submission')}
              className="bg-[#F55D3E] text-white px-6 py-2 rounded-lg hover:bg-[#F9A594] transition-colors"
            >
              Submit A New Study
            </button>
          </div>

          {submissions.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No submissions yet. Click "Submit A New Study" to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {submissions.map((submission) => (
                <div 
                  key={submission.id} 
                  className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6 flex flex-col"
                >
                  <div className="flex-1">
                    <div className={`w-full h-2 ${getStatusColor(submission.status)} rounded-t-lg -mt-6 -mx-6 mb-4`}></div>
                    <h3 className="text-lg font-medium">
                      {submission.data && typeof submission.data === 'object' && 'protocol_title' in submission.data 
                        ? (submission.data as any).protocol_title 
                        : 'Unnamed Submission'}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {submission.data && typeof submission.data === 'object' && 'description' in submission.data
                        ? (submission.data as any).description 
                        : 'No description'}
                    </p>
                  </div>
                  <div className="mt-auto pt-4 flex items-center justify-between border-t border-gray-100">
                    <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(submission.status)}`}>
                      {submission.status.replace(/_/g, ' ')}
                    </span>
                    <button
                      onClick={() => navigate(`/submissions/${submission.id}`)}
                      className="text-[#F55D3E] hover:text-[#F9A594] text-sm font-medium"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default withIdleTimer(UserDashboard); 